import React, { useState, useRef, useEffect } from "react";

const operationsList = ["Addition", "Subtraction", "Multiplication", "Division"];

function randomColor() {
    const colors = [
        "#e63946",
        "#457b9d",
        "#2a9d8f",
        "#f4a261",
        "#6a4c93",
        "#ff006e",
        "#118ab2",
        "#06d6a0",
    ];

    return colors[Math.floor(Math.random() * colors.length)];
}

function getSign(operation) {
    switch (operation) {
        case "Addition":
            return "+";

        case "Subtraction":
            return "-";

        case "Multiplication":
            return "×";

        case "Division":
            return "÷";

        default:
            return "";
    }
}

function generateNumber(digits) {
    const min = Math.pow(10, digits - 1);
    const max = Math.pow(10, digits) - 1;

    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateNumbers(
    operation,
    rowCount,
    digitCount,
    mixDigits,
    allowNegative,
    firstRowDigitCount,
    secondRowDigitCount
) {
    /*
        Multiplication and Division use their own digit settings.

        numbers[0] = first row
        numbers[1] = second row
    */

    if (operation === "Multiplication" || operation === "Division") {
        const firstDigits = firstRowDigitCount;
        const secondDigits = secondRowDigitCount;

        /*
            DIVISION

            Generate:
                dividend ÷ divisor = whole number

            while guaranteeing that:
                dividend has exactly firstDigits
                divisor has exactly secondDigits
        */

        if (operation === "Division") {
            /*
                A positive whole-number quotient cannot be produced
                when the divisor has more digits than the dividend.

                Example:
                    2-digit ÷ 3-digit
                cannot produce a positive whole-number quotient.

                In that situation this problem simply cannot be generated.
            */

            if (firstDigits < secondDigits) {
                return null;
            }

            for (let attempt = 0; attempt < 100; attempt++) {
                const divisor = generateNumber(secondDigits);

                const minDividend = Math.pow(10, firstDigits - 1);
                const maxDividend = Math.pow(10, firstDigits) - 1;

                /*
                    Find a quotient that makes the dividend fall
                    exactly inside the required digit range.
                */

                const minQuotient = Math.max(
                    1,
                    Math.ceil(minDividend / divisor)
                );

                const maxQuotient = Math.floor(
                    maxDividend / divisor
                );

                if (minQuotient <= maxQuotient) {
                    const quotient =
                        Math.floor(
                            Math.random() *
                                (maxQuotient - minQuotient + 1)
                        ) + minQuotient;

                    const dividend = divisor * quotient;

                    return [dividend, divisor];
                }
            }

            return null;
        }

        /*
            MULTIPLICATION

            Exact digit count for each row.
        */

        return [
            generateNumber(firstDigits),
            generateNumber(secondDigits),
        ];
    }

    /*
        ADDITION / SUBTRACTION

        Existing behaviour is preserved.
    */

    let numbers = [];

    for (let i = 0; i < rowCount; i++) {
        const digits = mixDigits
            ? Math.floor(Math.random() * digitCount) + 1
            : digitCount;

        numbers.push(generateNumber(digits));
    }

    if (!allowNegative && operation === "Subtraction") {
        numbers.sort((a, b) => b - a);
    }

    return numbers;
}

function calculateAnswer(numbers, operation) {
    switch (operation) {
        case "Addition":
            return numbers.reduce((a, b) => a + b, 0);

        case "Subtraction":
            return numbers.reduce((a, b) => a - b);

        case "Multiplication":
            return numbers.reduce((a, b) => a * b, 1);

        case "Division":
            return numbers.reduce((a, b) => a / b);

        default:
            return "";
    }
}

function createProblemKey(numbers, operation) {
    let nums = [...numbers];

    /*
        Addition and multiplication are commutative,
        so sort numbers for uniqueness.
    */

    if (
        operation === "Addition" ||
        operation === "Multiplication"
    ) {
        nums.sort((a, b) => a - b);
    }

    return operation + "|" + nums.join(",");
}

export default function MathWorksheet() {
    const [problemCount] = useState(40);

    const [selectedOperations, setSelectedOperations] = useState([]);

    /*
        Used by Addition/Subtraction.
    */
    const [rowCount, setRowCount] = useState(2);
    const [digitCount, setDigitCount] = useState(2);

    /*
        Used independently by Multiplication/Division.
    */
    const [firstRowDigitCount, setFirstRowDigitCount] = useState(2);
    const [secondRowDigitCount, setSecondRowDigitCount] = useState(2);

    const [mixDigits, setMixDigits] = useState(false);
    const [allowNegative, setAllowNegative] = useState(false);
    const [showAnswers, setShowAnswers] = useState(false);

    const [problems, setProblems] = useState([]);

    const [activeTab, setActiveTab] = useState("generator");

    const [history, setHistory] = useState([]);

    const printRef = useRef();

    /* ------------------ Load History ------------------ */

    useEffect(() => {
        const saved = localStorage.getItem("worksheetHistory");

        if (saved) {
            setHistory(JSON.parse(saved));
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(
            "worksheetHistory",
            JSON.stringify(history)
        );
    }, [history]);

    /* ------------------ Toggle Operation ------------------ */

    const toggleOperation = (op) => {
        setSelectedOperations((prev) =>
            prev.includes(op)
                ? prev.filter((o) => o !== op)
                : [...prev, op]
        );
    };

    /* ------------------ Generate Problems ------------------ */

    const generateProblems = () => {
        if (selectedOperations.length === 0) {
            setProblems([]);
            return;
        }

        /*
            Prevent attempting impossible whole-number Division problems.
        */

        if (
            selectedOperations.includes("Division") &&
            firstRowDigitCount < secondRowDigitCount
        ) {
            window.alert(
                "For Division, the First Row must have at least as many digits as the Second Row."
            );

            return;
        }

        const newProblems = [];

        const uniqueSet = new Set();

        let attempts = 0;

        const maxAttempts = problemCount * 20;

        while (
            newProblems.length < problemCount &&
            attempts < maxAttempts
        ) {
            attempts++;

            const operation =
                selectedOperations[
                    Math.floor(
                        Math.random() *
                            selectedOperations.length
                    )
                ];

            /*
                Multiplication and Division always have exactly
                two rows.
            */

            const rowsForThisProblem =
                operation === "Multiplication" ||
                operation === "Division"
                    ? 2
                    : rowCount;

            const numbers = generateNumbers(
                operation,
                rowsForThisProblem,
                digitCount,
                mixDigits,
                allowNegative,
                firstRowDigitCount,
                secondRowDigitCount
            );

            /*
                Division can return null when a valid whole-number
                problem cannot be generated.
            */

            if (!numbers) {
                continue;
            }

            const key = createProblemKey(
                numbers,
                operation
            );

            if (uniqueSet.has(key)) {
                continue;
            }

            uniqueSet.add(key);

            newProblems.push({
                operation,
                numbers,
                answer: calculateAnswer(
                    numbers,
                    operation
                ),
                borderColor: randomColor(),
                numberColors: numbers.map(() =>
                    randomColor()
                ),
            });
        }

        setProblems(newProblems);

        /*
            Save generated worksheet into history.
        */

        if (newProblems.length > 0) {
            const firstTwo =
                newProblems[0].numbers
                    .slice(0, 2)
                    .join("-");

            const sheet = {
                id: Date.now(),
                name: `${firstTwo} | ${selectedOperations.join(
                    ", "
                )}`,
                problems: newProblems,
                createdAt: new Date().toLocaleString(),
            };

            setHistory((prev) => [
                sheet,
                ...prev,
            ]);
        }
    };

    /* ------------------ History Actions ------------------ */

    const clearHistory = () => {
        if (
            window.confirm(
                "Clear all worksheet history?"
            )
        ) {
            setHistory([]);
        }
    };

    const openHistorySheet = (sheet) => {
        const previousShowAnswers =
            showAnswers;

        setProblems(sheet.problems);

        /*
            Force answers visible for print.
        */
        setShowAnswers(true);

        setActiveTab("generator");

        setTimeout(() => {
            window.print();

            /*
                Restore previous answer visibility.
            */
            setShowAnswers(
                previousShowAnswers
            );
        }, 300);
    };

    /* ------------------ Toggle Button Component ------------------ */

    const ToggleButton = ({
        label,
        active,
        onClick,
    }) => (
        <button
            onClick={onClick}
            style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: active
                    ? "2px solid #39a9e6"
                    : "2px solid #ccc",
                background: active
                    ? "#39a9e6"
                    : "white",
                color: active
                    ? "white"
                    : "black",
                cursor: "pointer",
                fontWeight: 500,
            }}
        >
            {label}
        </button>
    );

    const hasMultiplicationOrDivision =
        selectedOperations.some(
            (op) =>
                op === "Multiplication" ||
                op === "Division"
        );

    return (
        <div
            style={{
                padding: 30,
                fontFamily: "Arial",
            }}
        >
            {/* ---------------- Tabs ---------------- */}

            <div
                style={{
                    display: "flex",
                    gap: 20,
                    marginBottom: 20,
                }}
            >
                <button
                    onClick={() =>
                        setActiveTab("generator")
                    }
                    style={tabStyle(
                        activeTab === "generator"
                    )}
                >
                    Worksheet Generator
                </button>

                <button
                    onClick={() =>
                        setActiveTab("history")
                    }
                    style={tabStyle(
                        activeTab === "history"
                    )}
                >
                    History
                </button>
            </div>

            {/* ================= GENERATOR TAB ================= */}

            {activeTab === "generator" && (
                <>
                    <h2>
                        Math Worksheet Generator
                    </h2>

                    {/* OPERATIONS PANEL */}

                    <div style={panelStyle}>
                        <h3>Operations</h3>

                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                flexWrap: "wrap",
                            }}
                        >
                            {operationsList.map(
                                (op) => {
                                    const active =
                                        selectedOperations.includes(
                                            op
                                        );

                                    return (
                                        <button
                                            key={op}
                                            onClick={() =>
                                                toggleOperation(
                                                    op
                                                )
                                            }
                                            style={{
                                                padding:
                                                    "8px 14px",
                                                borderRadius: 6,
                                                border: active
                                                    ? "2px solid #2a9d8f"
                                                    : "2px solid #ccc",
                                                background:
                                                    active
                                                        ? "#2a9d8f"
                                                        : "white",
                                                color:
                                                    active
                                                        ? "white"
                                                        : "black",
                                                cursor:
                                                    "pointer",
                                                fontWeight:
                                                    500,
                                            }}
                                        >
                                            {op}
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>

                    {/* SETTINGS PANEL */}

                    <div style={panelStyle}>
                        <h3>Settings</h3>

                        {/* Addition/Subtraction Row Count */}

                        <div
                            style={{
                                display: "flex",
                                gap: 15,
                                flexWrap: "wrap",
                            }}
                        >
                            <strong>
                                Row Count:
                            </strong>

                            {[2, 3, 4, 5, 6].map(
                                (n) => (
                                    <button
                                        key={n}
                                        onClick={() =>
                                            setRowCount(
                                                n
                                            )
                                        }
                                        style={rowDigitStyle(
                                            rowCount ===
                                                n
                                        )}
                                    >
                                        {n}
                                    </button>
                                )
                            )}
                        </div>

                        {/* Addition/Subtraction Digit Count */}

                        <div
                            style={{
                                display: "flex",
                                gap: 15,
                                flexWrap: "wrap",
                                marginTop: 20,
                            }}
                        >
                            <strong>
                                Digit Count:
                            </strong>

                            {[
                                1,
                                2,
                                3,
                                4,
                                5,
                                6,
                            ].map((n) => (
                                <button
                                    key={n}
                                    onClick={() =>
                                        setDigitCount(
                                            n
                                        )
                                    }
                                    style={rowDigitStyle(
                                        digitCount ===
                                            n
                                    )}
                                >
                                    {n}
                                </button>
                            ))}

                            <span
                                style={{
                                    alignSelf:
                                        "center",
                                    color:
                                        "#666",
                                    fontSize:
                                        13,
                                }}
                            >
                                Used for
                                Addition/Subtraction
                            </span>
                        </div>

                        {/* Multiplication / Division Digit Controls */}

                        {hasMultiplicationOrDivision && (
                            <div
                                style={{
                                    marginTop: 20,
                                    paddingTop: 15,
                                    borderTop:
                                        "1px solid #ccc",
                                }}
                            >
                                <strong>
                                    Multiplication /
                                    Division Digit
                                    Count:
                                </strong>

                                {/* FIRST ROW */}

                                <div
                                    style={{
                                        display:
                                            "flex",
                                        gap: 15,
                                        flexWrap:
                                            "wrap",
                                        alignItems:
                                            "center",
                                        marginTop:
                                            12,
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth:
                                                100,
                                        }}
                                    >
                                        First Row:
                                    </strong>

                                    {[
                                        1,
                                        2,
                                        3,
                                        4,
                                        5,
                                        6,
                                    ].map(
                                        (n) => (
                                            <button
                                                key={`first-${n}`}
                                                onClick={() =>
                                                    setFirstRowDigitCount(
                                                        n
                                                    )
                                                }
                                                style={rowDigitStyle(
                                                    firstRowDigitCount ===
                                                        n
                                                )}
                                            >
                                                {n}
                                            </button>
                                        )
                                    )}
                                </div>

                                {/* SECOND ROW */}

                                <div
                                    style={{
                                        display:
                                            "flex",
                                        gap: 15,
                                        flexWrap:
                                            "wrap",
                                        alignItems:
                                            "center",
                                        marginTop:
                                            12,
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth:
                                                100,
                                        }}
                                    >
                                        Second Row:
                                    </strong>

                                    {[
                                        1,
                                        2,
                                        3,
                                        4,
                                        5,
                                        6,
                                    ].map(
                                        (n) => (
                                            <button
                                                key={`second-${n}`}
                                                onClick={() =>
                                                    setSecondRowDigitCount(
                                                        n
                                                    )
                                                }
                                                style={rowDigitStyle(
                                                    secondRowDigitCount ===
                                                        n
                                                )}
                                            >
                                                {n}
                                            </button>
                                        )
                                    )}
                                </div>

                                <div
                                    style={{
                                        marginTop: 10,
                                        color:
                                            "#666",
                                        fontSize:
                                            13,
                                    }}
                                >
                                    For
                                    Multiplication:
                                    First Row ×
                                    Second Row.
                                    For Division:
                                    First Row ÷
                                    Second Row.
                                </div>

                                {selectedOperations.includes(
                                    "Division"
                                ) &&
                                    firstRowDigitCount <
                                        secondRowDigitCount && (
                                        <div
                                            style={{
                                                marginTop: 10,
                                                color:
                                                    "#b00020",
                                                fontSize:
                                                    13,
                                            }}
                                        >
                                            For Division,
                                            the First
                                            Row must
                                            have at
                                            least as
                                            many digits
                                            as the
                                            Second Row
                                            to generate
                                            whole-number
                                            division
                                            problems.
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* TOGGLE SETTINGS */}

                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                marginTop: 20,
                            }}
                        >
                            <ToggleButton
                                label="Mix Digits"
                                active={
                                    mixDigits
                                }
                                onClick={() =>
                                    setMixDigits(
                                        !mixDigits
                                    )
                                }
                            />

                            <ToggleButton
                                label="Allow Negative"
                                active={
                                    allowNegative
                                }
                                onClick={() =>
                                    setAllowNegative(
                                        !allowNegative
                                    )
                                }
                            />

                            <ToggleButton
                                label="Show Answers"
                                active={
                                    showAnswers
                                }
                                onClick={() =>
                                    setShowAnswers(
                                        !showAnswers
                                    )
                                }
                            />
                        </div>
                    </div>

                    {/* GENERATE */}

                    <button
                        onClick={
                            generateProblems
                        }
                        style={buttonStyle}
                    >
                        Generate New Problems
                    </button>

                    {/* PRINT */}

                    <button
                        onClick={() =>
                            window.print()
                        }
                        style={buttonStyle}
                    >
                        Print / Save as PDF
                    </button>

                    {/* PRINT AREA */}

                    <div ref={printRef}>
                        <div
                            id="printArea"
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(5, minmax(0, 1fr))",
                                gap: "15px",
                            }}
                        >
                            {problems.map(
                                (p, idx) => {
                                    const isMultiplication =
                                        p.operation ===
                                        "Multiplication";

                                    /*
                                        IMPORTANT:

                                        Current space under the black
                                        line is treated as ONE row.

                                        Therefore:

                                            second-row digit count = x

                                        gives:

                                            x + 1 rows

                                        beneath the black line.

                                        Example:

                                            2-digit multiplier
                                                = 3 rows

                                            3-digit multiplier
                                                = 4 rows
                                    */

                                    const multiplicationGuideRows =
                                        isMultiplication
                                            ? p.numbers[1]
                                                  .toString()
                                                  .replace(
                                                      "-",
                                                      ""
                                                  )
                                                  .length +
                                              1
                                            : 0;

                                    return (
                                        <div
                                            key={idx}
                                            className="problem-card"
                                            style={{
                                                border: `2px solid ${p.borderColor}`,
                                                padding:
                                                    "8px",
                                                pageBreakInside:
                                                    "avoid",
                                            }}
                                        >
                                            {/* PROBLEM NUMBERS */}

                                            <div>
                                                {p.numbers.map(
                                                    (
                                                        num,
                                                        i
                                                    ) => (
                                                        <div
                                                            key={
                                                                i
                                                            }
                                                            style={{
                                                                color: p
                                                                    .numberColors[
                                                                    i
                                                                ],
                                                                fontSize:
                                                                    20,
                                                                textAlign:
                                                                    "right",
                                                                minHeight:
                                                                    24,
                                                            }}
                                                        >
                                                            {i ===
                                                            0
                                                                ? num
                                                                : `${getSign(
                                                                      p.operation
                                                                  )} ${num}`}
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                            {/* BLACK LINE */}

                                            <div
                                                style={{
                                                    borderTop:
                                                        "2px solid black",
                                                    marginTop:
                                                        6,
                                                }}
                                            />

                                            {/* MULTIPLICATION WORKING AREA */}

                                            {isMultiplication ? (
                                                <div
                                                    className="multiplication-work-area"
                                                    style={{
                                                        marginTop:
                                                            0,
                                                    }}
                                                >
                                                    {Array.from(
                                                        {
                                                            length: multiplicationGuideRows,
                                                        },
                                                        (
                                                            _,
                                                            rowIndex
                                                        ) => {
                                                            const isFinalRow =
                                                                rowIndex ===
                                                                multiplicationGuideRows -
                                                                    1;

                                                            return (
                                                                <div
                                                                    key={
                                                                        rowIndex
                                                                    }
                                                                    className="multiplication-guide-row"
                                                                    style={{
                                                                        /*
                                                                            Each guide row is the
                                                                            same height as the
                                                                            original answer space.

                                                                            This preserves the
                                                                            original scale while
                                                                            adding the required
                                                                            working space.
                                                                        */
                                                                        height: 22,

                                                                        boxSizing:
                                                                            "border-box",

                                                                        /*
                                                                            Horizontal guide
                                                                            line after each
                                                                            working row.
                                                                        */
                                                                       borderBottom:
                                                                            rowIndex >= multiplicationGuideRows - 2
                                                                                ? "2px solid black"
                                                                                : "1px solid #999",
                                                                        textAlign:
                                                                            "right",

                                                                        fontSize:
                                                                            20,

                                                                        lineHeight:
                                                                            "21px",
                                                                    }}
                                                                >
                                                                    {/*
                                                                        Only the LAST row is
                                                                        the final answer row.
                                                                    */}

                                                                    {isFinalRow &&
                                                                    showAnswers
                                                                        ? p.answer
                                                                        : ""}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            ) : (
                                                /*
                                                    Addition,
                                                    Subtraction
                                                    and Division
                                                    retain the original
                                                    answer space.
                                                */

                                                <div
                                                    style={{
                                                        height: 22,
                                                        textAlign:
                                                            "right",
                                                        fontSize:
                                                            20,
                                                    }}
                                                >
                                                    {showAnswers
                                                        ? p.answer
                                                        : ""}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ================= HISTORY TAB ================= */}

            {activeTab === "history" && (
                <>
                    <h2>
                        Worksheet History
                    </h2>

                    <button
                        onClick={
                            clearHistory
                        }
                        style={buttonStyle}
                    >
                        Clear All History
                    </button>

                    <div
                        style={{
                            border:
                                "1px solid #ccc",
                            marginTop: 20,
                        }}
                    >
                        {history.length ===
                            0 && (
                            <p
                                style={{
                                    padding: 10,
                                }}
                            >
                                No history yet.
                            </p>
                        )}

                        {history.map(
                            (sheet) => (
                                <div
                                    key={
                                        sheet.id
                                    }
                                    onDoubleClick={() =>
                                        openHistorySheet(
                                            sheet
                                        )
                                    }
                                    style={{
                                        padding:
                                            10,
                                        borderBottom:
                                            "1px solid #eee",
                                        cursor:
                                            "pointer",
                                    }}
                                >
                                    <strong>
                                        {
                                            sheet.name
                                        }
                                    </strong>

                                    <div
                                        style={{
                                            fontSize:
                                                12,
                                            color:
                                                "#666",
                                        }}
                                    >
                                        {
                                            sheet.createdAt
                                        }
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </>
            )}

            {/* ================= PRINT CSS ================= */}

            <style>{`
            @media print {
              body * {
                visibility: hidden;
              }

              #printArea,
              #printArea * {
                visibility: visible;
              }

              #printArea {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                background: white;
              }

              @page {
                size: A4;
                margin: 12mm;
              }
            }
            `}</style>
        </div>
    );
}

/* ================= STYLES ================= */

const panelStyle = {
    background: "#f3f3f3",
    border: "1px solid #ccc",
    padding: 15,
    marginBottom: 20,
};

const buttonStyle = {
    marginRight: 15,
    marginBottom: 20,
    padding: "8px 18px",
    fontSize: 15,
};

const rowDigitStyle = (active) => ({
    padding: "6px 12px",
    borderRadius: 6,
    border: active
        ? "2px solid #457b9d"
        : "2px solid #ccc",
    background: active
        ? "#457b9d"
        : "white",
    color: active
        ? "white"
        : "black",
    cursor: "pointer",
});

const tabStyle = (active) => ({
    padding: "10px 18px",
    borderRadius: 6,
    border: active
        ? "2px solid #2a9d8f"
        : "2px solid #ccc",
    background: active
        ? "#2a9d8f"
        : "white",
    color: active
        ? "white"
        : "black",
    cursor: "pointer",
    fontWeight: 600,
});